import React, { createContext, useContext, useEffect, useState } from 'react';
import { casesApi } from '../services/cases';
import { CaseFile } from '../types';

interface CaseContextType {
  selectedCase: CaseFile | null;
  setSelectedCase: (case_: CaseFile | null) => void;
  availableCases: CaseFile[];
  loadingCases: boolean;
  refreshCases: () => Promise<void>;
}

const CaseContext = createContext<CaseContextType | undefined>(undefined);

export const useCaseContext = () => {
  const context = useContext(CaseContext);
  if (context === undefined) {
    throw new Error('useCaseContext must be used within a CaseProvider');
  }
  return context;
};

interface CaseProviderProps {
  children: React.ReactNode;
}

export const CaseProvider: React.FC<CaseProviderProps> = ({ children }) => {
  const [selectedCase, setSelectedCase] = useState<CaseFile | null>(null);
  const [availableCases, setAvailableCases] = useState<CaseFile[]>([]);
  const [loadingCases, setLoadingCases] = useState(true);

  const loadCases = async () => {
    try {
      setLoadingCases(true);
      const response = await casesApi.getCases();
      
      // Transform backend cases to frontend format, only include uploaded cases
      const transformedCases: CaseFile[] = ((response as any).cases || [])
        .filter((case_: any) => case_.status === 'uploaded' && case_.pdf_file_name && case_.pdf_file_size)
        .map((case_: any) => ({
          id: case_.case_id,
          name: case_.pdf_file_name!,
          size: case_.pdf_file_size!,
          type: 'pdf',
          uploadedAt: case_.created_at || new Date().toISOString(),
          status: case_.status === 'uploaded' ? 'completed' : case_.status || 'completed',
        }))
        .sort((a: CaseFile, b: CaseFile) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime());

      setAvailableCases(transformedCases);
      
      // Auto-select first case if none selected OR if selected case no longer exists
      if (transformedCases.length > 0) {
        // Check if currently selected case still exists in the list
        const selectedCaseExists = selectedCase && transformedCases.some(c => c.id === selectedCase.id);
        
        if (!selectedCaseExists) {
          // If no case selected or selected case was deleted, auto-select the first (most recent) case
          console.log(`🔄 Auto-selecting most recent case: ${transformedCases[0].id}`);
          setSelectedCase(transformedCases[0]);
        }
      } else {
        // No cases available, clear selection
        if (selectedCase) {
          console.log('🔄 No cases available, clearing selection');
          setSelectedCase(null);
        }
      }
    } catch (error) {
      console.error('Failed to load cases:', error);
      setAvailableCases([]);
    } finally {
      setLoadingCases(false);
    }
  };

  const refreshCases = async () => {
    await loadCases();
  };

  useEffect(() => {
    loadCases();
  }, []);

  const contextValue: CaseContextType = {
    selectedCase,
    setSelectedCase,
    availableCases,
    loadingCases,
    refreshCases,
  };

  return (
    <CaseContext.Provider value={contextValue}>
      {children}
    </CaseContext.Provider>
  );
};
